import {
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add,
  Archive,
  ArrowBack,
  ArrowUpward,
  ArrowDownward,
  DeleteForever,
  Edit,
  Group,
  Refresh,
} from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { taskRequest } from "./api";
import BoardMembersDialog from "./BoardMembersDialog";
import BoardPicker from "./BoardPicker";
import TaskDialog, { userName } from "./TaskDialog";
import { formatDeadline, sortTasks } from "./taskSorting";

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", minWidth: 0 },
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    margin: "16px 0",
  },
  board: {
    display: "flex",
    gap: 16,
    overflowX: "auto",
    alignItems: "flex-start",
    paddingBottom: 24,
    minHeight: 280,
  },
  list: {
    flex: "0 0 300px",
    maxWidth: "85vw",
    padding: 12,
    backgroundColor: theme.palette.background.default,
    border: "2px solid transparent",
  },
  over: { borderColor: theme.palette.primary.main },
  listHeader: { display: "flex", alignItems: "center", marginBottom: 8 },
  card: { marginBottom: 10, cursor: "grab" },
  dropBefore: { boxShadow: `inset 0 3px ${theme.palette.primary.main}` },
  dropAfter: { boxShadow: `inset 0 -3px ${theme.palette.primary.main}` },
  description: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    marginTop: 8,
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 12 },
  empty: { padding: "24px 8px", textAlign: "center" },
}));

export default function Tasks() {
  const [selectedBoard, setSelectedBoard] = useState(null);
  return selectedBoard ? (
    <TaskBoardView
      key={selectedBoard.id}
      selectedBoard={selectedBoard}
      onBack={() => setSelectedBoard(null)}
    />
  ) : (
    <BoardPicker onSelect={setSelectedBoard} />
  );
}

function TaskBoardView({ selectedBoard, onBack }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [board, setBoard] = useState({ lists: [], tasks: [], users: [] });
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selection, setSelection] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [listEditor, setListEditor] = useState(null);
  const [listError, setListError] = useState("");
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [sortModes, setSortModes] = useState({});
  const [reversedLists, setReversedLists] = useState({});
  const requestId = useRef(0);
  const mutation = useRef(false);
  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const result = await taskRequest("get_task_board", {
        boardId: selectedBoard.id,
        archived,
      });
      if (current === requestId.current) setBoard(result);
    } catch (err) {
      if (current === requestId.current) setError(err.message);
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [archived, selectedBoard.id]);
  useEffect(() => {
    load();
    return () => {
      requestId.current += 1;
    };
  }, [load]);

  const saveList = async () => {
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setListError("");
    try {
      await taskRequest("save_task_list", {
        boardId: selectedBoard.id,
        listId: listEditor.id,
        name: listEditor.name,
      });
      setListEditor(null);
      await load();
    } catch (err) {
      setListError(err.message);
    } finally {
      setBusy(false);
      mutation.current = false;
    }
  };
  const deleteList = async () => {
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setListError("");
    try {
      await taskRequest("delete_task_list", {
        boardId: selectedBoard.id,
        listId: listEditor.id,
      });
      setListEditor(null);
      await load();
    } catch (err) {
      setListError(err.message);
    } finally {
      setBusy(false);
      mutation.current = false;
    }
  };
  const repositionTask = async (taskId, listId, beforeTaskId = null) => {
    if (mutation.current || archived || loading) return;
    const task = board.tasks.find((item) => item.id === taskId);
    if (!task || taskId === beforeTaskId || (sortModes[listId] || "manual") !== "manual") return;
    // The API uses saved ascending order; translate the visible insertion point when reversed.
    if (reversedLists[listId]) {
      const visible = sortTasks(board.tasks.filter((item) => item.TaskListId === listId && !item.archived && item.id !== taskId), "manual", true);
      const index = beforeTaskId === null ? visible.length : visible.findIndex((item) => item.id === beforeTaskId);
      if (index < 0) return;
      beforeTaskId = index > 0 ? visible[index - 1].id : null;
    }
    mutation.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await taskRequest("move_task", {
        taskId,
        listId,
        beforeTaskId,
        revision: task.revision,
      });
      setBoard((previous) => ({
        ...previous,
        tasks: previous.tasks.map((item) => result.tasks.find((updated) => updated.id === item.id) || item)
          .concat(result.tasks.filter((updated) => !previous.tasks.some((item) => item.id === updated.id))),
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      mutation.current = false;
    }
  };
  const moveTask = (event, listId, beforeTaskId = null) => {
    event.preventDefault();
    event.stopPropagation();
    const taskId = Number(event.dataTransfer.getData("application/x-task"));
    setOver(null);
    setDragging(null);
    setDropTarget(null);
    repositionTask(taskId, listId, beforeTaskId);
  };

  return (
    <div className={classes.root}>
      <Breadcrumbs>
        <Link
          color="inherit"
          disabled={busy}
          onClick={onBack}
          style={{ cursor: "pointer" }}
        >
          {t("tasks.boards")}
        </Link>
        <Typography>{selectedBoard.name}</Typography>
      </Breadcrumbs>
      <div className={classes.toolbar}>
        <Typography component="h1" variant="h5" style={{ flexGrow: 1 }}>
          {selectedBoard.name}
          {archived ? ` — ${t("tasks.archiveHeader")}` : ""}
        </Typography>
        <Button startIcon={<ArrowBack />} disabled={busy} onClick={onBack}>
          {t("tasks.backToBoards")}
        </Button>
        <Button
          startIcon={<Group />}
          disabled={busy || loading}
          onClick={() => setShowMembers(true)}
        >
          {t("tasks.members")}
        </Button>
        <Button
          startIcon={<Archive />}
          disabled={busy || loading}
          onClick={() => setArchived(!archived)}
        >
          {t(archived ? "tasks.backToBoard" : "tasks.showArchive")}
        </Button>
        <IconButton
          aria-label={t("tasks.refresh")}
          disabled={busy || loading}
          onClick={load}
        >
          <Refresh />
        </IconButton>
        {!archived && (
          <Button
            color="primary"
            variant="contained"
            startIcon={<Add />}
            disabled={busy || loading}
            onClick={() => {
              setListError("");
              setListEditor({ name: "" });
            }}
          >
            {t("tasks.addList")}
          </Button>
        )}
      </div>
      {error && (
        <Alert severity="error" role="alert" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}
      {busy && (
        <Typography role="status" color="textSecondary">
          {t("tasks.saving")}
        </Typography>
      )}
      {loading ? (
        <CircularProgress aria-label={t("tasks.loading")} />
      ) : (
        <>
          {!board.lists.length && !error && (
            <Paper className={classes.empty}>
              <Typography>{t("tasks.emptyBoard")}</Typography>
            </Paper>
          )}
          {archived && !board.tasks.length && (
            <Typography className={classes.empty}>
              {t("tasks.emptyArchive")}
            </Typography>
          )}
          <div className={classes.board}>
            {board.lists.map((list) => {
              const sortMode = sortModes[list.id] || "manual";
              const reversed = !!reversedLists[list.id];
              const cards = sortTasks(board.tasks.filter(
                (task) =>
                  task.TaskListId === list.id && !!task.archived === archived,
              ), sortMode, reversed);
              if (archived && !cards.length) return null;
              return (
                <Paper
                  component="section"
                  aria-label={list.name}
                  key={list.id}
                  className={`${classes.list} ${over === list.id ? classes.over : ""}`}
                  onDragOver={(event) => {
                    if (dragging && !archived && !busy && sortMode === "manual") {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                      setOver(list.id);
                      setDropTarget(null);
                    }
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget))
                      setOver(null);
                  }}
                  onDrop={(event) => moveTask(event, list.id)}
                >
                  <div className={classes.listHeader}>
                    <Typography
                      component="h2"
                      variant="subtitle1"
                      style={{ flexGrow: 1, overflowWrap: "anywhere" }}
                    >
                      {list.name} ({cards.length})
                    </Typography>
                    {!archived && (
                      <IconButton
                        size="small"
                        aria-label={t("tasks.renameList")}
                        disabled={busy}
                        onClick={() => {
                          setListError("");
                          setListEditor({ id: list.id, name: list.name });
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
                    <TextField select fullWidth variant="outlined" margin="dense" id={`task-sort-${list.id}`}
                      label={t("tasks.sortBy")} value={sortMode} disabled={busy}
                      SelectProps={{ native: true }}
                      helperText={sortMode !== "manual" && !archived ? t("tasks.manualOrderHint") : ""}
                      onChange={(event) => setSortModes({ ...sortModes, [list.id]: event.target.value })}>
                      <option value="manual">{t(reversed ? "tasks.sortManualReverse" : "tasks.sortManual")}</option>
                      <option value="deadline">{t(reversed ? "tasks.sortDeadlineReverse" : "tasks.sortDeadline")}</option>
                      <option value="title">{t(reversed ? "tasks.sortTitleReverse" : "tasks.sortTitle")}</option>
                    </TextField>
                    <IconButton aria-label={`${t("tasks.reverseOrder")}: ${list.name}`} title={t("tasks.reverseOrder")}
                      aria-pressed={reversed} disabled={busy} style={{ marginTop: 8 }}
                      onClick={() => setReversedLists((previous) => ({ ...previous, [list.id]: !previous[list.id] }))}>
                      {reversed ? <ArrowDownward /> : <ArrowUpward />}
                    </IconButton>
                  </div>
                  {cards.map((task, index) => (
                    <Card
                      key={task.id}
                      data-task-id={task.id}
                      className={`${classes.card} ${dropTarget && dropTarget.id === task.id ? (dropTarget.after ? classes.dropAfter : classes.dropBefore) : ""}`}
                      draggable={!busy && !archived && sortMode === "manual"}
                      style={{ opacity: dragging === task.id ? 0.5 : 1, backgroundColor: task.backgroundColor || undefined, color: task.color || undefined }}
                      onDragOver={(event) => {
                        if (!dragging || busy || archived || sortMode !== "manual") return;
                        event.preventDefault();
                        event.stopPropagation();
                        const rect = event.currentTarget.getBoundingClientRect();
                        setOver(list.id);
                        setDropTarget({ id: task.id, after: rect.height > 0 && event.clientY > rect.top + rect.height / 2 });
                      }}
                      onDrop={(event) => {
                        const rect = event.currentTarget.getBoundingClientRect();
                        const after = rect.height > 0 && event.clientY > rect.top + rect.height / 2;
                        const movedId = Number(event.dataTransfer.getData("application/x-task"));
                        const next = cards.slice(index + 1).find((item) => item.id !== movedId);
                        moveTask(event, list.id, after ? (next ? next.id : null) : task.id);
                      }}
                      onDragStart={(event) => {
                        event.dataTransfer.setData(
                          "application/x-task",
                          String(task.id),
                        );
                        event.dataTransfer.effectAllowed = "move";
                        setDragging(task.id);
                      }}
                      onDragEnd={() => {
                        setDragging(null);
                        setOver(null);
                        setDropTarget(null);
                      }}
                    >
                      <CardActionArea
                        disabled={busy}
                        onClick={() => setSelection({ id: task.id })}
                      >
                        <CardContent>
                          <Typography
                            style={{
                              fontWeight: 600,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {task.title}
                          </Typography>
                          {task.description && (
                            <Typography
                              color={task.color ? "inherit" : "textSecondary"}
                              variant="body2"
                              className={classes.description}
                            >
                              {task.description}
                            </Typography>
                          )}
                          {task.deadline && <Typography variant="body2" color={task.color ? "inherit" : "textSecondary"} style={{ marginTop: 8 }}>
                            {t("tasks.deadline")}: {formatDeadline(task.deadline)}
                          </Typography>}
                          {!!(task.Assignees || []).length && (
                            <div className={classes.chips}>
                              {task.Assignees.map((user) => (
                                <Chip
                                  key={user.id}
                                  size="small"
                                  variant={task.backgroundColor || task.color ? "outlined" : "default"}
                                  style={{ color: task.color || undefined, borderColor: task.color || undefined }}
                                  label={userName(user)}
                                />
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </CardActionArea>
                      {!archived && sortMode === "manual" && <CardActions style={{ justifyContent: "flex-end", paddingTop: 0 }}>
                        <IconButton size="small" color={task.color ? "inherit" : "default"} aria-label={`${t("tasks.moveUp")}: ${task.title}`} disabled={busy || index === 0}
                          onClick={() => repositionTask(task.id, list.id, cards[index - 1].id)}><ArrowUpward fontSize="small" /></IconButton>
                        <IconButton size="small" color={task.color ? "inherit" : "default"} aria-label={`${t("tasks.moveDown")}: ${task.title}`} disabled={busy || index === cards.length - 1}
                          onClick={() => repositionTask(task.id, list.id, cards[index + 2] ? cards[index + 2].id : null)}><ArrowDownward fontSize="small" /></IconButton>
                      </CardActions>}
                    </Card>
                  ))}
                  {!cards.length && (
                    <Typography
                      color="textSecondary"
                      variant="body2"
                      className={classes.empty}
                    >
                      {t("tasks.emptyList")}
                    </Typography>
                  )}
                  {!archived && (
                    <Button
                      fullWidth
                      startIcon={<Add />}
                      disabled={busy}
                      onClick={() => setSelection({ TaskListId: list.id })}
                    >
                      {t("tasks.addCard")}
                    </Button>
                  )}
                </Paper>
              );
            })}
          </div>
        </>
      )}
      {showMembers && (
        <BoardMembersDialog
          board={selectedBoard}
          onClose={() => setShowMembers(false)}
          onChanged={load}
        />
      )}
      {selection && (
        <TaskDialog
          selection={selection}
          lists={board.lists}
          users={board.users}
          onClose={() => setSelection(null)}
          onChanged={load}
        />
      )}
      <Dialog
        open={!!listEditor}
        onClose={() => {
          if (!busy) setListEditor(null);
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="task-list-title"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            saveList();
          }}
        >
          <DialogTitle id="task-list-title">
            {t(
              listEditor && listEditor.id
                ? "tasks.renameList"
                : "tasks.addList",
            )}{" "}
            {listEditor && listEditor.id ? (
              <IconButton
                onClick={() => {
                  deleteList();
                }}
              >
                <DeleteForever></DeleteForever>
              </IconButton>
            ) : null}
          </DialogTitle>
          <DialogContent>
            {listError && <Alert severity="error">{listError}</Alert>}
            <TextField
              autoFocus
              required
              fullWidth
              id="task-list-name"
              label={t("tasks.listName")}
              value={listEditor ? listEditor.name : ""}
              disabled={busy}
              inputProps={{ maxLength: 255 }}
              onChange={(event) =>
                setListEditor({ ...listEditor, name: event.target.value })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setListEditor(null)}>
              {t("tasks.close")}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={busy || !listEditor || !listEditor.name.trim()}
            >
              {t("tasks.save")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}
